import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { X, Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Spinner from "@/components/Spinner";

interface ProjectRejectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (comments: string[]) => Promise<unknown>;
  isPending?: boolean;
}

export const ProjectRejectDialog = ({
  open,
  onOpenChange,
  onConfirm,
  isPending = false,
}: ProjectRejectDialogProps) => {
  const { t } = useTranslation();
  const [comments, setComments] = useState<string[]>([""]);

  const handleAddComment = useCallback(() => {
    setComments((prev) => [...prev, ""]);
  }, []);

  const handleRemoveComment = useCallback((index: number) => {
    setComments((prev) => {
      if (prev.length > 1) {
        return prev.filter((_, i) => i !== index);
      }
      return prev;
    });
  }, []);

  const handleCommentChange = useCallback((index: number, value: string) => {
    setComments((prev) => {
      const updatedComments = [...prev];
      updatedComments[index] = value;
      return updatedComments;
    });
  }, []);

  const handleConfirm = useCallback(async () => {
    const validComments = comments.filter((comment) => comment.trim() !== "");
    if (validComments.length > 0) {
      await onConfirm(validComments);
      setComments([""]);
      onOpenChange(false);
    }
  }, [comments, onConfirm, onOpenChange]);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        setComments([""]);
        onOpenChange(false);
      }
    },
    [onOpenChange],
  );

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const validComments = comments.filter((comment) => comment.trim() !== "");
  const canSubmit = validComments.length > 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {t("projects.actions.reject.confirm.title")}
          </DialogTitle>
          <DialogDescription>
            {t("projects.actions.reject.confirm.content")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">
                {t("projects.comments.title")}
              </label>
              <Button
                onClick={handleAddComment}
                size="sm"
                variant="ghost"
                disabled={isPending}
                className="h-8 w-8 p-0"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {comments.map((comment, index) => (
              <div key={index} className="flex gap-2 items-start">
                <Textarea
                  value={comment}
                  onChange={(e) => handleCommentChange(index, e.target.value)}
                  placeholder={t("projects.comments.addPlaceholder")}
                  rows={2}
                  className="flex-1"
                  disabled={isPending}
                />
                {comments.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveComment(index)}
                    className="text-gray-500 hover:text-red-600 hover:bg-red-50 mt-1"
                    disabled={isPending}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isPending}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!canSubmit || isPending}
          >
            {isPending ? (
              <>
                <Spinner />
                {t("common.loading")}
              </>
            ) : (
              t("projects.actions.reject.title")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
